import { useQuery } from '@tanstack/react-query';

import { ApiError, type ApiClient } from './client.js';
import { useApi } from './use-api.js';

/**
 * Browser-side Wiki types. Server-of-record is `packages/api/src/routes/wiki.ts`.
 * `source` and `memberCount` are tolerated as optional because older manifests
 * persisted by core may omit them — keep this defensive.
 */
export interface WikiManifestPage {
  readonly slug: string;
  readonly title: string;
  readonly source?: string;
  readonly memberCount?: number;
}

export interface WikiManifestResponse {
  readonly pages: readonly WikiManifestPage[];
  readonly generatedAt: string | null;
}

export async function fetchWikiManifest(
  api: ApiClient,
  slug: string,
): Promise<WikiManifestResponse> {
  return api.request<WikiManifestResponse>(`/api/repos/${encodeURIComponent(slug)}/wiki`);
}

/**
 * The page endpoint returns `text/markdown` (raw body), not JSON. We bypass
 * `ApiClient.request` and use `authHeader()` directly so we can read text.
 */
export async function fetchWikiPage(
  api: ApiClient,
  slug: string,
  pageSlug: string,
): Promise<string> {
  const headers = new Headers(await api.authHeader());
  const url = `${api.baseUrl}/api/repos/${encodeURIComponent(slug)}/wiki/${encodeURIComponent(pageSlug)}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    throw new ApiError(res.status, null, `${String(res.status)} ${res.statusText}`);
  }
  return res.text();
}

export function useWikiManifest(slug: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['wiki-manifest', slug],
    queryFn: () => fetchWikiManifest(api, slug ?? ''),
    enabled: Boolean(slug),
  });
}

export function useWikiPage(slug: string | undefined, pageSlug: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['wiki-page', slug, pageSlug],
    queryFn: () => fetchWikiPage(api, slug ?? '', pageSlug ?? ''),
    enabled: Boolean(slug) && Boolean(pageSlug),
  });
}
